import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TenantCtx = createContext(null);

export function TenantProvider({ children }) {
  const { user, tenant: authTenant } = useAuth();
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [tenants, setTenants] = useState([]);

  const loadTenants = useCallback(async () => {
    if (user?.role !== "superadmin") return;
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
      if (data.length && !activeTenantId) {
        // Prefer tenant with most assessed requirements; fallback to first
        const best = data.reduce((a, b) => ((b.assessed_count || 0) > (a.assessed_count || 0) ? b : a), data[0]);
        setActiveTenantId(best.id);
      }
    } catch { /* ignore */ }
  }, [user, activeTenantId]);

  useEffect(() => {
    if (!user) { setActiveTenantId(null); setTenants([]); return; }
    if (user.role === "superadmin") {
      loadTenants();
    } else {
      setActiveTenantId(user.tenant_id);
    }
  }, [user, loadTenants]);

  // helper: adds tenant_id query param for superadmin
  const withScope = (params = {}) => {
    if (user?.role === "superadmin" && activeTenantId) return { ...params, tenant_id: activeTenantId };
    return params;
  };

  const currentTenant =
    user?.role === "superadmin"
      ? tenants.find((t) => t.id === activeTenantId)
      : authTenant;

  return (
    <TenantCtx.Provider
      value={{ tenants, activeTenantId, setActiveTenantId, loadTenants, withScope, currentTenant }}
    >
      {children}
    </TenantCtx.Provider>
  );
}

export const useTenant = () => useContext(TenantCtx);
